import React, { useState, useMemo, useEffect, useCallback } from 'react';
import moment from "moment";
import { useListCustomersTopLevel, useListCustomerProfiles, AcceptedCommodity, acceptedCommodityTypes, RiskCropProfile, RiskCropProfileHistory, RiskManagementCustomerProfile } from '../../lib/api/risk/profiles';
import { custom, z } from 'zod';
import { Decimal } from 'decimal.js';
import {
  CashSalesFiltersValidation, RiskManagementCashSales, useListRiskCashSales, useListStagedCashSales,
  useProcessStagedRow, useDeleteStagedRow
} from '../../lib/api/risk/cashSales';
import { CommonDestinationTimezone, GenerateLoadingOrError, genFlatOptionList, getUniqueStrings } from '@/lib/utils';
import { Combobox } from '@/components/ui/combobox';
import { BearDecimalInput, BearIntInput } from '@/lib/form/ui';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import useDebounce from '../otc/useDebounce';
import { useSearchParams } from 'react-router-dom';
import '@/styles/riskCompactTable.css';
import { Badge } from "@/components/ui/badge";
import { X, ChevronDown, ChevronUp, DeleteIcon, Trash2Icon, CheckCheck, ArrowDownIcon, ArrowRightIcon } from "lucide-react";
import { DataTable } from "@/components/ui/virtualizedTable";
import { ColumnDef } from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import * as Collapsible from "@radix-ui/react-collapsible";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ChevronRight, Search, Plus, Edit } from "lucide-react";
import { CreateCropProfileDialog, EditCropProfileDialog } from './CropProfileForm';
import { useListCustomers } from '@/lib/api/customer';
import { cn } from '@/lib/utils';
import { formatCommodityToUom, formatNumberToDisplay, safeParseDecimal, sortOptionMonths } from '@/lib/api/common';
import { Separator } from '@/components/ui/separator';
import { Commodities, CommodityOptions, Uom } from '@/lib/api/commodity';
import { RiskProfileDetailModal } from '@/components/modals/RiskProfileDetailModal';
import { CropProfileReportModal } from '@/section/risk/GenerateProfileReportModal';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import SummaryCard from './SummaryCard';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import RiskManagementSummaryTables from './SummaryTables';
import { useListTraders } from '@/lib/api/trader';
import { Trader } from '@/lib/api/reports';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ContractType, generateContractTypesWith } from '@/lib/api/generalContract';
import { UploadCashReportDialog } from './UploadCashReportDialog';
import { DatePicker } from '@/components/ui/typeable_date_picker';
import { useToast } from "@/components/ui/use-toast";
import { Label } from '@/components/ui/label';
import { BinaryCropTypeSelection, useListRiskSummary, RiskManagementSummaryResult, useListRiskSummaryFullRows, BinaryCropTypeSelectionOptions } from '@/lib/api/risk/summary';
import { createArrayOfSize } from '@/lib/freightReport/frieghtReportFunctions';
import { ContractTypeOptions, ContractType as OtcContractType } from '@/lib/api/otc';
import { RiskCashSummaryCommodityLevel } from './CashSummaryCommodityLevel';
import { GeneralContractSection, OtcContractSection, RjoSection } from './RiskSummaryFullRowsLevel';
import { Loader } from '@/components/ui/loader';
import { AllCustomerTodos } from './AllCustomerTodos';
import { CustomerActivitiesSection } from '@/components/modals/CustomerActivitiesSection';

type ExpandedState = Record<string, boolean>;

interface AccountingSummaryRow {
  id: string;
  commodity: string;
  commodityId: string;
  projected_production: Decimal;
}

interface TableRow {
  id: string;
  commodity: string;
  commodityId: string;
  customerId: string;
  customerName: string;
  hta: Decimal | null;
  unpxdBasis: Decimal | null;
}

interface OtcTableRow {
  id: string;
  commodity: string;
  commodityId: string;
  customerId: string;
  customerName: string;
  otcId: string;
  otcContract: any;
}

interface CommoditySummaryRow {
  id: string;
  commodity: string;
  commodityId: string;
  customer_id: string;
  customer_name: string;
  projected_production: string | Decimal;
  hta_volume: string | Decimal;
  basis_volume: string | Decimal;
  beacon_volume: string | Decimal;
  beacon_quantity: string | Decimal;
  rjo_percent_hedged: string | Decimal;
  without_beacon_percent_hedged: string | Decimal;
  beacon_percent_hedged: string | Decimal;
  rjo_options: string;
  otc_contracts: Record<string, Decimal | string>;
}

type FiltersState = z.infer<typeof CashSalesFiltersValidation>;

interface SearchFilterState {
  searchTerm: string;
  showFilters: boolean;
}

type TabType = "summary" | "corn" | "soybeans" | "canola" | "wheat";

export function RiskManagementSummaryDashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentTab, setTab] = useState<TabType>(searchParams.get("tab") as any || 'summary');
  const [traderIds, setTraderIds] = useState<string[]>([]);
  const [customerIds, setCustomerIds] = useState<string[]>(searchParams.getAll("customer_ids"));
  const [selectedCropYear, setSelectedCropYear] = useState<string>(searchParams.get("crop_year") || moment.tz(CommonDestinationTimezone).format("YYYY"));
  const tradersResult = useListTraders();
  const customersResult = useListCustomers({ minimal: true, hasRiskProfile: true });
  const baseTopLevelResult = useListRiskSummary({ filters: { is_enabled: true, trader_ids: traderIds, customer_ids: customerIds, crop_year: parseInt(selectedCropYear) } });

  useEffect(() => {
    setSearchParams((searchParams) => {
      searchParams.delete("customer_ids");
      searchParams.set("crop_year", selectedCropYear);
      customerIds.forEach(customer_id => searchParams.append("customer_ids", customer_id));

      return searchParams;
    })
  }, [customerIds, selectedCropYear]);

  const data: RiskManagementSummaryResult | undefined = baseTopLevelResult?.data?.data;
  const isLoadingContent: boolean = baseTopLevelResult.isLoading || baseTopLevelResult.isFetching;
  const tradersList: Trader[] = tradersResult?.data?.data || [];
  const customersList: { id: string; long_name: string; hasRiskProfile: boolean }[] = (customersResult?.data?.data || []);

  const { CropYearOptions } = useMemo(() => {
    const currentYear = parseInt(moment.tz(CommonDestinationTimezone).subtract(1, 'year').format("YYYY"));
    const years = createArrayOfSize(4).map((_, index: number) => currentYear + index).map(item => item.toString());
    return { CropYearOptions: years.map(year => ({ value: year, label: year })) }
  }, []);

  const { traderOptions, customerOptions } = useMemo(() => {
    return {
      traderOptions: tradersList.map(trader => ({ value: trader.id, label: trader.name })).sort((a, b) => a.label.localeCompare(b.label)),
      customerOptions: customersList.map(customer => ({ value: customer.id, label: customer.long_name })).sort((a, b) => a.label.localeCompare(b.label)),
    }
  }, [tradersList, customersList]);

  const loadingOrErrorUi = GenerateLoadingOrError([baseTopLevelResult, tradersResult]);
  if (loadingOrErrorUi) return loadingOrErrorUi;

  const setSpecificParam = (paramName: string, paramValue: string) => {
    setSearchParams(prevParams => {
      const newParams = new URLSearchParams(prevParams);
      newParams.set(paramName, paramValue);
      return newParams;
    });
  };

  const onTabChange = (tab: TabType) => {
    setTab(tab);
    setSpecificParam("tab", tab);
  };

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-2xl font-bold">Risk Cash Summary</h1>
      <div className="flex items-center gap-2 cursor-pointer">
        <div className="relative">
          <label htmlFor="tradersFilter" className="block text-sm font-medium text-gray-700">Trader</label>
          <Combobox
            id="tradersFilter"
            dwimOnChange
            virtualizeList
            options={traderOptions}
            value={""}
            onValueChange={(val) => setTraderIds(getUniqueStrings(traderIds.includes(val) ? traderIds.filter(item => item !== val) : [...traderIds, val]).sort())}
            placeholder="Select Trader"
            className="w-full"
            popoverClassName='md:min-w-[300px] lg:min-w-[400px] xl:min-w-[500px]'
          />
        </div>
        <div className="relative">
          <label htmlFor="customerFilter" className="block text-sm font-medium text-gray-700">Customer</label>
          <Combobox
            id="customerFilter"
            dwimOnChange
            virtualizeList
            options={customerOptions}
            value={""}
            onValueChange={(val) => setCustomerIds(getUniqueStrings(customerIds.includes(val) ? customerIds.filter(item => item !== val) : [...customerIds, val]).sort())}
            placeholder="Select Customer"
            className="w-full"
            popoverClassName='md:min-w-[300px] lg:min-w-[400px] xl:min-w-[500px]'
          />
        </div>
        <div className="relative">
          <label htmlFor="cropYearFilter" className="block text-sm font-medium text-gray-700">Crop Year</label>
          <Combobox
            id="cropYearFilter"
            dwimOnChange
            virtualizeList
            options={CropYearOptions}
            value={selectedCropYear || ""}
            onValueChange={(val) => setSelectedCropYear(val || moment.tz(CommonDestinationTimezone).format("YYYY"))}
            className="w-full"
            popoverClassName='md:min-w-[300px] lg:min-w-[400px] xl:min-w-[500px]'
          />
        </div>
        <div className="flex flex-wrap gap-2 mb-4 items-center md:ml-4">
          {(!!traderIds?.length) && (traderIds.map(traderId => (
            <Badge variant="secondary" className="flex items-center gap-1" key={`traderFilterTag:${traderId}`}>
              {tradersList.find(trader => trader.id === traderId)?.name || ""}
              <X className="h-3 w-3 cursor-pointer" onClick={() => setTraderIds(traderIds.filter(item => item !== traderId))} />
            </Badge>
          ))
          )}
          {(!!customerIds?.length) && (customerIds.map((id: string) => (
            <Badge variant="secondary" className="flex items-center gap-1" key={`customerFilterTag:${id}`}>
              {customersList.find(customer => customer.id === id)?.long_name || ""}
              <X className="h-3 w-3 cursor-pointer" onClick={() => setCustomerIds(customerIds.filter(item => item !== id))} />
            </Badge>
          ))
          )}
        </div>
      </div>
      <Tabs value={currentTab} onValueChange={(tab) => onTabChange(tab as TabType)} >
        <TabsList className="mb-4">
          <TabsTrigger value="summary">Summary</TabsTrigger>
          <TabsTrigger value="corn">Corn</TabsTrigger>
          <TabsTrigger value="soybeans">Soybeans</TabsTrigger>
          <TabsTrigger value="wheat">Wheat</TabsTrigger>
          <TabsTrigger value="canola">Canola</TabsTrigger>
          <TabsTrigger value="activities">Activities</TabsTrigger>
          <TabsTrigger value="loader" disabled>
            <div className="flex flex-row gap-2 items-center justify-center w-[80px]">
              {isLoadingContent ? (
                <span className=""><Loader /></span>
              ) : (
                <span className="text-muted-foreground flex flex-row gap-2"><CheckCheck /><span>Loaded</span></span>
              )}
            </div>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="summary">
          <RiskCashSummaryTopLevel data={data} tradersList={tradersList} />
        </TabsContent>
        <TabsContent value="corn">
          <RiskCashSummaryCommodityLevel commodities={[Commodities.Corn]} data={data} />
        </TabsContent>
        <TabsContent value="soybeans">
          <RiskCashSummaryCommodityLevel commodities={[Commodities.Soybeans]} data={data} />
        </TabsContent>
        <TabsContent value="wheat">
          <RiskCashSummaryCommodityLevel commodities={[Commodities.MinneapolisWheat, Commodities.WinterWheat, Commodities.WinterWheatPrelimCommoditySymbol]} data={data} />
        </TabsContent>
        <TabsContent value="canola">
          <RiskCashSummaryCommodityLevel commodities={[Commodities.Canola]} data={data} />
        </TabsContent>
        <TabsContent value="activities">
          <CustomerActivitiesSection customerId={customerIds?.length > 0 ? customerIds[0] : undefined} traderId={traderIds?.length > 0 ? traderIds[0] : undefined} showCustomerNames />
        </TabsContent>
      </Tabs>
    </div>
  )
}

export default RiskManagementSummaryDashboard;

function RiskCashSummaryTopLevel({ data, tradersList }: { data: RiskManagementSummaryResult | undefined; tradersList: Trader[] }) {
  return (
    <div className="space-y-2 gap-4 max-w-[90%]">
      <CommoditySummary data={data} />
      <GeneralContractSection data={data} tradersList={tradersList} />
      <OtcContractSection data={data} tradersList={tradersList} />
      <RjoSection data={data} tradersList={tradersList} />
    </div>
  );
}

function CommoditySummary({ data }: { data: RiskManagementSummaryResult | undefined }) {
  const { all_rows, grouped_keys, hasRowValues } = useMemo(() => {
    if (!data?.summary) {
      return { rowGroups: {}, grouped_keys: [], hasRowValues: false };
    }

    let hasRowValues = false;
    const allRows: Record<string, AccountingSummaryRow[]> = {};
    const { commodity: commodityData, commodity_customer_map } = data.summary;

    Object.entries(commodityData || {}).forEach(([commodityId, commoditySummary]) => {
      if (!allRows[commodityId]) { allRows[commodityId] = []; }
      const commodityDisplay = CommodityOptions.find(option => option.value === commodityId)?.label || commodityId;
      const commodityRow = Object.entries(commoditySummary || {}).reduce((acc, item) => {
        if (!hasRowValues) { hasRowValues = true; }
        acc.projected_production = acc.projected_production.plus(item[1].projected_production);
        acc.actual_production = acc.actual_production.plus(item[1].actual_production);
        acc.acreage = acc.acreage.plus(item[1].acreage);
        return acc;
      }, { id: commodityId, commodityId, commodity: commodityDisplay, projected_production: new Decimal(0), actual_production: new Decimal(0), acreage: new Decimal(0) });

      allRows[commodityId].push(commodityRow);
    });

    Object.entries(allRows).forEach(([_key, rows]: any) => {
      rows.sort((a: TableRow, b: TableRow) => {
        return a.commodity.localeCompare(b.commodity);
      })
    })

    const grouped_keys = getUniqueStrings(Object.keys(allRows).filter(item => allRows[item]?.length > 0)).sort();

    // Sort by commodity, then by customer name
    return {
      grouped_keys,
      all_rows: grouped_keys?.flatMap(item => allRows[item]) || [],
      hasRowValues
    };
  }, [data]);

  if (!data) return null;

  const columns: any[] = [
    {
      accessorKey: 'commodity',
      header: ({ table }: any) => (
        <>
          <button
            {...{
              onClick: table.getToggleAllRowsExpandedHandler(),
            }}
          >
            {table.getIsAllRowsExpanded() ? <ArrowDownIcon className="w-4 h-4" /> : <ArrowRightIcon className="w-4 h-4" />}
          </button>{' '}
          Commodity
        </>
      ),
      cell: ({ row, getValue }: any) => (
        <div
          style={{
            // Since rows are flattened by default,
            // we can use the row.depth property
            // and paddingLeft to visually indicate the depth
            // of the row
            paddingLeft: `${row.depth * 2}rem`,
          }}
        >
          <div>
            {row.getCanExpand() ? (
              <button
                {...{
                  onClick: row.getToggleExpandedHandler(),
                  style: { cursor: 'pointer' },
                }}
              >
                {row.getIsExpanded() ? <ArrowDownIcon className="w-4 h-4" /> : <ArrowRightIcon className="w-4 h-4" />}
              </button>
            ) : (null)}{' '}
            {getValue()}
          </div>
        </div>
      ),
      footer: (props: any) => props.column.id,
      enableColumnFilter: false,
    },
    {
      accessorKey: 'acreage',
      header: 'Acreage',
      width: 150,
      aggregationFn: 'decimalAggregate',
      cell: ({ row }: any) => { return formatNumberToDisplay(row.original.acreage, { emptyFormat: "--" }); },
      aggregatedCell: ({ row, getValue }: any) => { return formatNumberToDisplay(getValue(), { emptyFormat: "--" }) },
      enableColumnFilter: false,
    },
    {
      accessorKey: 'projected_production',
      header: 'Projected Production',
      width: 150,
      aggregationFn: 'decimalAggregate',
      cell: ({ row }: any) => { return formatNumberToDisplay(row.original.projected_production, { emptyFormat: "--", suffix: row.original.commodityId === Commodities.Canola ? Uom.Lbs : Uom.Bushel }); },
      aggregatedCell: ({ row, getValue }: any) => { return formatNumberToDisplay(getValue(), { emptyFormat: "--", suffix: row.original.commodity === Commodities.Canola ? Uom.Lbs : Uom.Bushel }) },
      enableColumnFilter: false,
    },
    {
      accessorKey: 'actual_production',
      header: 'Actual Production',
      width: 150,
      aggregationFn: 'decimalAggregate',
      cell: ({ row }: any) => { return formatNumberToDisplay(row.original.actual_production, { emptyFormat: "--", suffix: row.original.commodityId === Commodities.Canola ? Uom.Lbs : Uom.Bushel }); },
      aggregatedCell: ({ row, getValue }: any) => { return formatNumberToDisplay(getValue(), { emptyFormat: "--", suffix: row.original.commodity === Commodities.Canola ? Uom.Lbs : Uom.Bushel }) },
      enableColumnFilter: false,
    },
  ];


  return (
    <div className="">
      <div className="text-xl font-semibold mb-4">Commodity Summary</div>
      <DataTable
        height="600px"
        columns={columns}
        data={all_rows!}
        className={`mx-4`}
      />
    </div>
  );
}
